import { useEffect, useMemo, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { toast } from "sonner";
import { CreditCard, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";

function InnerForm({ orderId, amount, onPaid }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true);
    try {
      const { error, paymentIntent } = await stripe.confirmPayment({ elements, redirect: "if_required" });
      if (error) { toast.error(error.message || "Pagamento rifiutato"); return; }
      await api.post(`/orders/${orderId}/card/confirm`, { payment_intent_id: paymentIntent.id });
      toast.success("Carta autorizzata · l'addebito avviene alla conferma consegna");
      onPaid?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="card-payment-form">
      <PaymentElement options={{ layout: "tabs" }} />
      <div className="flex items-start gap-2 text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-2">
        <ShieldCheck className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" />
        L'importo viene solo <b>bloccato</b> sulla carta (Visa, Mastercard). L'addebito effettivo avviene alla conferma della consegna o automaticamente allo scadere del termine (1h Express/Offerte, 6h asta).
      </div>
      <Button type="submit" disabled={!stripe || busy} className="w-full ga-btn-primary rounded-full" data-testid="card-pay-submit">
        <CreditCard className="w-4 h-4 mr-2" />{busy ? "Autorizzazione..." : `Autorizza € ${Number(amount).toFixed(2)}`}
      </Button>
    </form>
  );
}

export default function CardPaymentForm({ orderId, publishableKey, onPaid }) {
  const [intent, setIntent] = useState(null);
  const [err, setErr] = useState("");
  const stripePromise = useMemo(() => (publishableKey ? loadStripe(publishableKey) : null), [publishableKey]);

  useEffect(() => {
    api.post(`/orders/${orderId}/card/intent`).then(({ data }) => setIntent(data))
      .catch(e => setErr(formatApiError(e.response?.data?.detail) || "Impossibile avviare il pagamento"));
  }, [orderId]);

  if (err) return <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="card-payment-error">{err}</div>;
  if (!intent || !stripePromise) return <div className="text-sm text-slate-500 py-4">Caricamento modulo carta...</div>;
  return (
    <Elements stripe={stripePromise} options={{ clientSecret: intent.client_secret, locale: "it", appearance: { theme: "stripe", variables: { colorPrimary: "#059669", borderRadius: "10px" } } }}>
      <InnerForm orderId={orderId} amount={intent.amount} onPaid={onPaid} />
    </Elements>
  );
}
